import { whitelistService } from './whitelist.service';
import type { User, AuthError, WhitelistUser } from '../interfaces/apiInterfaces/auth';
import { globalConfig } from '../config/globalConfig';

export const authService = {
  /**
   * Validate if email has @nagwa.com domain
   */
  validateEmailDomain(email: string): boolean {
    return email.toLowerCase().endsWith(globalConfig.nagwaEmailDomain);
  },

  /**
   * Convert WhitelistUser to User
   */
  mapWhitelistUserToUser(whitelistUser: WhitelistUser): User {
    return {
      id: whitelistUser.user_id,
      email: whitelistUser.user_email,
      firstName: whitelistUser.user_first_name,
      lastName: whitelistUser.user_last_name,
      roles: whitelistUser.roles
    };
  },

  /**
   * Validate user login with Google email
   * 1. Check @nagwa.com domain
   * 2. Check if email is whitelisted
   * @returns User object if valid, throws AuthError if invalid
   */
  async validateUserLogin(email: string): Promise<User> {
    // Step 1: Validate email domain
    if (!this.validateEmailDomain(email)) {
      const error: AuthError = {
        type: 'INVALID_DOMAIN',
        message: 'Only @nagwa.com emails are allowed'
      };
      throw error;
    }

    try {
      // Step 2: Check if email is whitelisted
      const whitelistUser = await whitelistService.checkUserWhitelist(email);

      if (!whitelistUser) {
        const error: AuthError = {
          type: 'NOT_WHITELISTED',
          message: 'Email not registered'
        };
        throw error;
      }

      // Convert and return user
      return this.mapWhitelistUserToUser(whitelistUser);
    } catch (error) {
      // If it's already an AuthError, rethrow it
      if ((error as AuthError).type) {
        throw error;
      }

      // Otherwise, wrap as network error
      const authError: AuthError = {
        type: 'NETWORK_ERROR',
        message: 'Unable to verify user access. Please try again.'
      };
      throw authError;
    }
  }
};
